import { useRef, useCallback } from 'react';
import Editor, { Monaco, OnMount } from '@monaco-editor/react';

// Monaco editor instance type
type MonacoEditor = Parameters<OnMount>[0];

interface SqlEditorProps {
  value: string;
  onChange: (value: string) => void;
  onExecute?: () => void;
  height?: string;
  readOnly?: boolean;
}

export function SqlEditor({
  value,
  onChange,
  onExecute,
  height = '300px',
  readOnly = false,
}: SqlEditorProps) {
  const editorRef = useRef<MonacoEditor | null>(null);

  const handleEditorDidMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;

    // Add Ctrl+Enter to execute
    if (onExecute) {
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
        onExecute();
      });
    }

    // Configure SQL language settings
    monaco.languages.registerCompletionItemProvider('sql', {
      provideCompletionItems: (model: Parameters<typeof monaco.editor.getModel>[0], position: { lineNumber: number; column: number }) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        // SQL keywords
        const keywords = [
          'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'NOT', 'IN', 'LIKE', 'BETWEEN',
          'JOIN', 'LEFT', 'RIGHT', 'INNER', 'OUTER', 'ON', 'AS', 'ORDER', 'BY',
          'ASC', 'DESC', 'GROUP', 'HAVING', 'LIMIT', 'OFFSET', 'UNION', 'ALL',
          'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE', 'CREATE', 'ALTER',
          'DROP', 'TABLE', 'INDEX', 'VIEW', 'PRIMARY', 'KEY', 'FOREIGN', 'REFERENCES',
          'NULL', 'NOT NULL', 'DEFAULT', 'AUTO_INCREMENT', 'DISTINCT', 'COUNT', 'SUM',
          'AVG', 'MIN', 'MAX', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'IF', 'IFNULL',
          'COALESCE', 'CAST', 'CONVERT', 'DATE', 'NOW', 'CURDATE', 'YEAR', 'MONTH', 'DAY',
        ];

        // TARK-specific table suggestions
        const tables = [
          'tark_user', 'tark_squad', 'tark_squad_membership', 'tark_squad_message',
          'tark_play_session', 'tark_user_report', 'tark_squad_report', 'tark_role',
          'tark_zip_master', 'tark_court', 'tark_court_unit', 'tark_provider',
        ];

        const suggestions = [
          ...keywords.map((keyword) => ({
            label: keyword,
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: keyword,
            range,
          })),
          ...tables.map((table) => ({
            label: table,
            kind: monaco.languages.CompletionItemKind.Class,
            insertText: table,
            detail: 'Table',
            range,
          })),
        ];

        return { suggestions };
      },
    });

    // Focus the editor
    editor.focus();
  }, [onExecute]);

  const handleEditorChange = useCallback((value: string | undefined) => {
    onChange(value || '');
  }, [onChange]);

  const handleBeforeMount = useCallback((monaco: Monaco) => {
    // Define custom dark theme
    monaco.editor.defineTheme('sql-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: '22d3ee', fontStyle: 'bold' }, // cyan
        { token: 'string', foreground: 'a5f3fc' }, // light cyan
        { token: 'number', foreground: 'fbbf24' }, // amber
        { token: 'comment', foreground: '64748b', fontStyle: 'italic' }, // slate
        { token: 'operator', foreground: 'f472b6' }, // pink
      ],
      colors: {
        'editor.background': '#0f172a', // slate-900
        'editor.foreground': '#e2e8f0', // slate-200
        'editor.lineHighlightBackground': '#1e293b', // slate-800
        'editor.selectionBackground': '#334155', // slate-700
        'editorCursor.foreground': '#22d3ee', // cyan
        'editorLineNumber.foreground': '#475569', // slate-600
        'editorLineNumber.activeForeground': '#94a3b8', // slate-400
      },
    });
  }, []);

  return (
    <div className="rounded-lg overflow-hidden border border-slate-700">
      <Editor
        height={height}
        language="sql"
        value={value}
        onChange={handleEditorChange}
        onMount={handleEditorDidMount}
        beforeMount={handleBeforeMount}
        theme="sql-dark"
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          readOnly,
          automaticLayout: true,
          tabSize: 2,
          suggest: {
            showKeywords: true,
          },
          quickSuggestions: true,
          padding: { top: 12, bottom: 12 },
        }}
      />
    </div>
  );
}
